import { useState, type FormEvent } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCredentialSave } from "@/lib/hooks/useCredentialSave";
import type { AffiliateCredentialPublic } from "@/lib/types";
import { CredentialHealth, CredentialShell, SaveFeedback } from "./CredentialShell";

const ERRORS: Record<string, string> = {
  magalu_store_or_promoter_required: "Informe o nome da loja, o ID de divulgador ou os dois.",
  invalid_magalu_slug: "Nome da loja inválido. Use letras minúsculas, números, hífen ou underline.",
  invalid_magalu_promoter_id: "ID de divulgador deve ter só números.",
  invalid_magalu_partner_id: "ID de parceiro deve ter só números.",
  tenant_not_found: "Conta não encontrada. Entre de novo no painel.",
};

interface Props {
  readonly credential: AffiliateCredentialPublic | undefined;
}

/** Magalu: nome da loja (Magazine Você) + ID de divulgador (Parceiro Magalu). */
export function MagaluCredentialSection({ credential }: Props): JSX.Element {
  const config = credential?.config_public ?? {};
  const [storeName, setStoreName] = useState(typeof config["storeName"] === "string" ? config["storeName"] : "");
  const [promoterId, setPromoterId] = useState(typeof config["promoterId"] === "string" ? config["promoterId"] : "");
  const [partnerId, setPartnerId] = useState(typeof config["partnerId"] === "string" ? config["partnerId"] : "");
  const { saving, status, error, save } = useCredentialSave("magalu", ERRORS);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    await save("upsert_magalu_affiliate_credential_v2", {
      p_store_name: storeName.trim().toLowerCase(),
      p_promoter_id: promoterId.trim(),
      p_partner_id: partnerId.trim() || null,
    });
  };

  return (
    <CredentialShell id="magalu" title="Magalu">
      <div className="space-y-1.5">
        <p className="text-sm font-semibold text-foreground">Loja e ID de divulgador</p>
        <p className="text-xs text-muted-foreground">
          Links do magazinevoce.com.br trocam o nome da loja pelo seu. Links do magazineluiza.com.br recebem o seu ID
          de divulgador (promoter_id). Cada conta aceita uma loja Magalu; o link sai direto, sem encurtador.
        </p>
      </div>
      <CredentialHealth credential={credential} />

      <form onSubmit={handleSubmit} className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="magalu-store" variant="field">Nome da loja</Label>
          <Input id="magalu-store" value={storeName} onChange={(e) => setStoreName(e.target.value)} placeholder="joaodasilva" autoComplete="off" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="magalu-promoter" variant="field">ID de divulgador</Label>
          <Input id="magalu-promoter" value={promoterId} onChange={(e) => setPromoterId(e.target.value)} placeholder="123456" inputMode="numeric" autoComplete="off" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="magalu-partner" variant="field">ID de parceiro (opcional)</Label>
          <Input id="magalu-partner" value={partnerId} onChange={(e) => setPartnerId(e.target.value)} placeholder="partner_id dos seus links" inputMode="numeric" autoComplete="off" />
        </div>
        <div className="flex justify-end sm:col-span-3">
          <Button type="submit" variant="gold" disabled={saving}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
            Salvar e testar
          </Button>
        </div>
      </form>
      <SaveFeedback status={status} error={error} />
    </CredentialShell>
  );
}
